/**
 * BOM versions list + slide-out inline BOM lines editor (18.9).
 */

import { useCallback, useEffect, useState } from 'react';
import useEntityList from '../../hooks/useEntityList';
import {
  fetchBomVersions, fetchBomLinesForVersion,
  createBomLineForVersion, updateBomLineForVersion, deleteBomLineForVersion,
} from '../../api/parentItems';
import { fetchParts } from '../../api/parts';
import { SlideOutPanel, Button, LoadingSpinner, EmptyState, Pagination } from '../common';
import SearchableSelect from '../common/SearchableSelect';

export default function BomVersionList({ search, showDeleted }) {
  const { items, loading, totalCount, page, setPage, refresh } = useEntityList(fetchBomVersions, { search, showDeleted });
  const [selected, setSelected] = useState(null);
  const [lines, setLines] = useState([]);
  const [linesLoading, setLinesLoading] = useState(false);
  const [addingLine, setAddingLine] = useState(false);
  const [newLine, setNewLine] = useState({ part: '', qty_per_parent: 1, sort_order: 0 });
  // Server-side parts search (same pattern as BomTab). The bulk-load
  // `availableParts` + `partsLoaded` pair was replaced because it
  // silently dropped parts past page_size=500.
  const [partsSearch, setPartsSearch] = useState('');
  const [partsResults, setPartsResults] = useState([]);
  const [partsLoading, setPartsLoading] = useState(false);
  const [selectedPartObj, setSelectedPartObj] = useState(null);

  async function openEdit(item) {
    setSelected(item);
    setLinesLoading(true);
    try {
      const resp = await fetchBomLinesForVersion({ bom_version: item.id, page_size: 200 });
      setLines(resp.data || resp.results || resp || []);
    } catch {
      setLines([]);
    } finally {
      setLinesLoading(false);
    }
  }

  // Debounced server-side parts search. Same pattern as BomTab — 300ms
  // debounce, 25 results per query, includes the currently-selected
  // part in results so the trigger keeps showing it across searches.
  useEffect(() => {
    if (!addingLine) return;
    const handle = setTimeout(async () => {
      setPartsLoading(true);
      try {
        const resp = await fetchParts({
          search: partsSearch || '',
          page_size: 25,
        });
        const list = resp.data || resp.results || resp || [];
        if (selectedPartObj && !list.find((p) => p.id === selectedPartObj.id)) {
          setPartsResults([selectedPartObj, ...list]);
        } else {
          setPartsResults(list);
        }
      } catch {
        setPartsResults(selectedPartObj ? [selectedPartObj] : []);
      } finally {
        setPartsLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [partsSearch, addingLine]); // eslint-disable-line react-hooks/exhaustive-deps

  function handlePartSelect(partId) {
    setNewLine((prev) => ({ ...prev, part: partId }));
    const pickedPart = partsResults.find((p) => p.id === partId) || null;
    setSelectedPartObj(pickedPart);
  }

  function closePanel() { setSelected(null); setLines([]); setAddingLine(false); }

  async function handleAddLine() {
    if (!newLine.part) { alert('Part is required'); return; }
    try {
      await createBomLineForVersion({
        bom_version: selected.id,
        part: newLine.part,
        qty_per_parent: newLine.qty_per_parent || 1,
        sort_order: newLine.sort_order || lines.length + 1,
      });
      setAddingLine(false);
      setNewLine({ part: '', qty_per_parent: 1, sort_order: 0 });
      // Reload lines
      const resp = await fetchBomLinesForVersion({ bom_version: selected.id, page_size: 200 });
      setLines(resp.data || resp.results || resp || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add line');
    }
  }

  async function handleDeleteLine(lineId) {
    if (!confirm('Remove this BOM line?')) return;
    try {
      await deleteBomLineForVersion(lineId);
      setLines((prev) => prev.filter((l) => l.id !== lineId));
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed');
    }
  }

  async function handleUpdateLine(lineId, field, value) {
    try {
      await updateBomLineForVersion(lineId, { [field]: value });
      setLines((prev) =>
        prev.map((l) => (l.id === lineId ? { ...l, [field]: value } : l))
      );
    } catch {
      // ignore
    }
  }

  if (loading) return <div className="flex justify-center py-10"><LoadingSpinner /></div>;

  return (
    <div>
      {items.length === 0 ? (
        <EmptyState title="No BOM versions" description="BOM versions are created from parent items." />
      ) : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-2">Parent Item</th>
                <th className="px-4 py-2">Version</th>
                <th className="px-4 py-2">Label</th>
                <th className="px-4 py-2 text-center">Default</th>
                <th className="px-4 py-2 text-center">Lines</th>
                <th className="px-4 py-2 text-right">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => openEdit(item)}
                  className="hover:bg-blue-50 cursor-pointer"
                >
                  <td className="px-4 py-2 text-foreground">{item.parent_item_display || item.parent_item || '—'}</td>
                  <td className="px-4 py-2 font-medium">v{item.version_number}</td>
                  <td className="px-4 py-2 text-gray-500">{item.version_label || '—'}</td>
                  <td className="px-4 py-2 text-center">
                    {item.is_default && <span className="text-blue-600 text-xs font-medium">Default</span>}
                  </td>
                  <td className="px-4 py-2 text-center text-gray-500">{item.lines_count ?? '—'}</td>
                  <td className="px-4 py-2 text-right text-gray-400 text-xs">{item.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2">
            <Pagination current={page} total={Math.ceil(totalCount / 200)} onChange={setPage} />
          </div>
        </>
      )}

      <SlideOutPanel
        open={!!selected}
        onClose={closePanel}
        title={`BOM Version: v${selected?.version_number || ''} ${selected?.version_label ? `(${selected.version_label})` : ''}`}
        width="max-w-2xl"
      >
        <div className="space-y-4">
          {/* Version info */}
          <div className="text-sm text-gray-500">
            Parent: {selected?.parent_item_display || selected?.parent_item || '—'}
            {selected?.is_default && <span className="ml-2 text-blue-600 font-medium">Default version</span>}
          </div>

          {/* BOM Lines editor */}
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-foreground">BOM Lines ({lines.length})</h4>
            <Button size="sm" variant="ghost" onClick={() => setAddingLine(true)}>+ Add Line</Button>
          </div>

          {linesLoading ? (
            <div className="flex justify-center py-4"><LoadingSpinner /></div>
          ) : lines.length === 0 ? (
            <p className="text-sm text-gray-400">No lines in this version.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                    <th className="px-2 py-1.5 w-10">#</th>
                    <th className="px-2 py-1.5">Part</th>
                    <th className="px-2 py-1.5">Description</th>
                    <th className="px-2 py-1.5 text-right w-24">Qty/Parent</th>
                    <th className="px-2 py-1.5 w-12" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {lines.map((line, i) => (
                    <tr key={line.id} className="hover:bg-gray-50">
                      <td className="px-2 py-1.5 text-gray-400">{line.sort_order || i + 1}</td>
                      <td className="px-2 py-1.5 font-medium text-foreground">
                        {line.part_display || line.part || '—'}
                      </td>
                      <td className="px-2 py-1.5 text-xs text-gray-500 truncate max-w-[200px]">
                        {line.part_description || '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <input
                          type="number"
                          className="w-16 border rounded px-1 py-0.5 text-right text-sm"
                          value={line.qty_per_parent}
                          onChange={(e) => handleUpdateLine(line.id, 'qty_per_parent', +e.target.value)}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <button
                          onClick={() => handleDeleteLine(line.id)}
                          className="text-red-500 hover:text-red-700 text-xs"
                        >
                          Del
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Add line form */}
          {addingLine && (
            <div className="bg-blue-50/50 rounded-md p-3 space-y-2">
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Part</label>
                <SearchableSelect
                  value={newLine.part}
                  onChange={(v) => handlePartSelect(v)}
                  options={partsResults.map((p) => ({
                    value: p.id,
                    label: `${p.internal_part_no} — ${p.description || '(no description)'}`,
                    searchText: [p.reference_part_number, p.all_vendor_part_numbers, p.preferred_vendor_display].filter(Boolean).join(' '),
                  }))}
                  onSearchChange={setPartsSearch}
                  serverSideSearch={true}
                  loading={partsLoading}
                  placeholder="Type to search…"
                  emptyMessage="No parts match — refine search"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-0.5">Qty/Parent</label>
                  <input
                    type="number"
                    className="w-full border rounded px-2 py-1 text-sm"
                    value={newLine.qty_per_parent}
                    onChange={(e) => setNewLine({ ...newLine, qty_per_parent: +e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-0.5">Sort Order</label>
                  <input
                    type="number"
                    className="w-full border rounded px-2 py-1 text-sm"
                    value={newLine.sort_order}
                    onChange={(e) => setNewLine({ ...newLine, sort_order: +e.target.value })}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddLine}>Add</Button>
                <Button size="sm" variant="ghost" onClick={() => setAddingLine(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
      </SlideOutPanel>
    </div>
  );
}

function TextInput({ label, value, onChange, placeholder = '' }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-0.5">{label}</label>
      <input
        type="text"
        className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}
